import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import classnames from 'classnames';
import { t } from 'i18n-js';

import { Expandable } from '~/components/atoms/Expandable';
import Input, { TInputProps } from '~/components/atoms/FormControls/Input';
import { ListContainer } from '~/components/atoms/ListContainer';
import ButtonClean from '~/components/ButtonClean';

import useBooleanState from '~/hooks/useBooleanState';

import { ReactComponent as ChevronLeft } from '~/assets/images/iconsLibrary/chevron-left.svg';

import styles from './SelectExpandable.module.scss';

type TSelectExpandableProps = Omit<TInputProps, 'icon'> & {
  options: readonly string[];
  translateValue?: boolean;
};

type TInputRef = HTMLInputElement | null;

function SelectRenderer(
  {
    options,
    onClick,
    onChange,
    className,
    inputWrapperClassName,
    onBlur,
    translateValue = true,
    ...props
  }: TSelectExpandableProps,
  refProps: Ref<HTMLInputElement>,
): JSX.Element {
  const [isFocused, { setTrue: setFocusedTrue, setFalse: setFocusedFalse }] =
    useBooleanState();
  const ref = useRef<TInputRef>(null);
  useImperativeHandle<TInputRef, TInputRef>(refProps, () => ref.current);
  const value = useRef('');

  const onClickWrapper = useCallback(
    (event) => {
      if (isFocused) {
        ref.current?.blur();
      } else {
        setFocusedTrue();
      }
      if (onClick) {
        onClick(event);
      }
    },
    [isFocused, onClick, setFocusedTrue],
  );

  const onChangeWrapper = useCallback(
    (event) => {
      if (onChange) {
        event.target.value = value.current;
        onChange(event);
      }
    },
    [onChange],
  );

  const onBlurWrapper = useCallback(
    (event) => {
      // when options required scrolling the click wouldn't register, it feels like a hack doing it by className
      if (!event.relatedTarget?.classList.contains(styles.option)) {
        setFocusedFalse();
      }
      if (onBlur) {
        onBlur(event);
      }
    },
    [setFocusedFalse, onBlur],
  );

  return (
    <>
      <Input
        autoComplete="off"
        {...props}
        ref={ref}
        className={classnames(styles.input, className)}
        inputWrapperClassName={classnames(
          styles.inputWrapper,
          isFocused && styles.isExpanded,
          inputWrapperClassName,
        )}
        onClick={onClickWrapper}
        onChange={onChangeWrapper}
        onBlur={onBlurWrapper}
        icon={
          <ChevronLeft
            className={classnames(
              styles.chevron,
              isFocused && styles.isExpanded,
            )}
          />
        }
      />
      <Expandable isExpanded={isFocused}>
        <ListContainer className={styles.list}>
          {options?.map((key) => (
            <li key={key}>
              <ButtonClean
                className={styles.option}
                onClick={() => {
                  const input = ref.current;
                  if (input) {
                    const newValue = translateValue ? t(key) : key;
                    input.value = newValue;
                    value.current = newValue;
                    // I'm not fully aware what it does but without it the onChane won't trigger
                    const nativeInputValueSetter =
                      Object.getOwnPropertyDescriptor(
                        window.HTMLInputElement.prototype,
                        'value',
                      )?.set;
                    nativeInputValueSetter?.call(input, '');

                    const event = new Event('input', { bubbles: true });
                    input.dispatchEvent(event);
                    setFocusedFalse();
                  }
                }}
              >
                {t(key)}
              </ButtonClean>
            </li>
          ))}
        </ListContainer>
      </Expandable>
    </>
  );
}

const SelectExpandable = forwardRef(SelectRenderer);

export default SelectExpandable;
